import type { Appointment, AppointmentParticipant, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { mapAppointmentStatusToCalendarStatus } from '@/features/calendar/appointmentToCalendarEvent';
import { setParticipantAttendanceStatus, getParticipantAttendanceStatus } from '@unify/fhir-model/resources/appointment';
import {
  CALENDAR_CLIENT_ATTENDANCE_OPTIONS,
  CALENDAR_CLIENT_ATTENDANCE_STATUS_VALUES,
  DEFAULT_CALENDAR_CLIENT_ATTENDANCE_STATUS,
  getCalendarClientAttendanceLabel,
  isCalendarClientAttendanceAbsent,
  isCalendarClientAttendancePresent,
  locationRequiredForClientAttendance,
  normalizeCalendarClientAttendanceStatus,
  type CalendarClientAttendanceStatus,
} from '@/lib/calendarClientAttendanceStatus';
import { getAllClientParticipantsFromAppointment } from './fhirHelpers';

export const PARTICIPANT_ATTENDANCE_GROUP_LINKID = 'participant-attendance' as const;

/** Calendar-aligned per-client attendance (same values as provider calendar). */
export type ParticipantAttendanceStatus = CalendarClientAttendanceStatus;

/** @deprecated Use {@link ParticipantAttendanceStatus} — kept for existing imports. */
export type ParticipantAttendanceFhirStatus = ParticipantAttendanceStatus;

/** @deprecated Renamed to {@link CALENDAR_CLIENT_ATTENDANCE_STATUS_VALUES}. */
export const PARTICIPANT_ATTENDANCE_FHIR_STATUSES = CALENDAR_CLIENT_ATTENDANCE_STATUS_VALUES;

export type ParticipantAttendanceStatusOption = {
  value: ParticipantAttendanceStatus;
  label: string;
  description: string;
};

export const PARTICIPANT_ATTENDANCE_STATUS_OPTIONS: ReadonlyArray<ParticipantAttendanceStatusOption> =
  CALENDAR_CLIENT_ATTENDANCE_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
    description: o.label,
  }));

export function isParticipantAttendanceStatus(
  value: string | null | undefined,
): value is ParticipantAttendanceStatus {
  return normalizeCalendarClientAttendanceStatus(value) != null;
}

/** @deprecated Use {@link isParticipantAttendanceStatus}. */
export function isParticipantAttendanceFhirStatus(
  value: string | null | undefined,
): value is ParticipantAttendanceFhirStatus {
  return isParticipantAttendanceStatus(value);
}

export function getParticipantAttendanceStatusLabel(
  status: string | null | undefined,
  fallback = 'Confirmed',
): string {
  return getCalendarClientAttendanceLabel(status, fallback);
}

export const DEFAULT_PARTICIPANT_ATTENDANCE_STATUS: ParticipantAttendanceStatus =
  DEFAULT_CALENDAR_CLIENT_ATTENDANCE_STATUS;

export { locationRequiredForClientAttendance as locationRequiredForParticipantAttendance };

function coerceParticipantAttendanceStatus(
  raw: string | null | undefined,
): ParticipantAttendanceStatus {
  return normalizeCalendarClientAttendanceStatus(raw) ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
}

function linkIdForPatient(patientId: string): string {
  return `${PARTICIPANT_ATTENDANCE_GROUP_LINKID}.${patientId}`;
}

export function parsePatientIdFromParticipantAttendanceLinkId(
  linkId: string,
): string | undefined {
  const prefix = `${PARTICIPANT_ATTENDANCE_GROUP_LINKID}.`;
  if (linkId.startsWith(prefix)) {
    return linkId.slice(prefix.length);
  }
  return undefined;
}

/** Resolve attendance for one Patient participant from extension, then appointment context. */
export function resolveParticipantAttendanceFromAppointmentParticipant(
  participant: AppointmentParticipant,
  appointment: Appointment,
): ParticipantAttendanceStatus {
  const fromExtension = normalizeCalendarClientAttendanceStatus(
    getParticipantAttendanceStatus(participant),
  );
  if (fromExtension) return fromExtension;

  if (participant.status?.toLowerCase() === 'declined') {
    return 'client-no-show';
  }
  if (participant.status?.toLowerCase() === 'tentative') {
    return 'proposed';
  }
  if (participant.status?.toLowerCase() === 'needs-action') {
    return 'scheduled';
  }

  const fromAppointment = mapAppointmentStatusToCalendarStatus(appointment.status);
  if (fromAppointment && fromAppointment !== 'entered-in-error') {
    return fromAppointment;
  }

  return DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
}

/**
 * Read participant → status map from a saved QuestionnaireResponse.
 */
export function getParticipantAttendanceFromResponseItems(
  items: QuestionnaireResponseItem[] | undefined,
): Record<string, ParticipantAttendanceStatus> {
  const out: Record<string, ParticipantAttendanceStatus> = {};
  const group = items?.find((i) => i.linkId === PARTICIPANT_ATTENDANCE_GROUP_LINKID);
  if (!group?.item) return out;
  for (const child of group.item) {
    const id = child.linkId ? parsePatientIdFromParticipantAttendanceLinkId(child.linkId) : undefined;
    if (!id) continue;
    const a = child.answer?.[0]?.valueString;
    out[id] = coerceParticipantAttendanceStatus(a);
  }
  return out;
}

function clientParticipantIdFromReference(ref: string | undefined): string | undefined {
  const trimmed = ref?.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith('Patient/')) return trimmed.slice('Patient/'.length).trim() || undefined;
  if (trimmed.startsWith('RelatedPerson/')) {
    return trimmed.slice('RelatedPerson/'.length).trim() || undefined;
  }
  return undefined;
}

/**
 * Attendance explicitly recorded on `Appointment.participant` (extension only, no
 * inference from appointment status). Includes Patient and RelatedPerson clients
 * (couples/family PART + ad-hoc participants). Outranks a stale note copy during
 * hydration / sign validation.
 */
export function getExplicitParticipantAttendanceFromAppointment(
  appointment: Appointment,
): Record<string, ParticipantAttendanceStatus> {
  const out: Record<string, ParticipantAttendanceStatus> = {};
  for (const p of appointment.participant ?? []) {
    const id = clientParticipantIdFromReference(p.actor?.reference);
    if (!id) continue;
    const status = normalizeCalendarClientAttendanceStatus(getParticipantAttendanceStatus(p));
    if (status) out[id] = status;
  }
  return out;
}

/** Stable signature of explicit attendance, for detecting out-of-band changes. */
export function participantAttendanceSignature(
  appointment: Appointment | null | undefined,
): string {
  if (!appointment) return '';
  const explicit = getExplicitParticipantAttendanceFromAppointment(appointment);
  return Object.keys(explicit)
    .sort()
    .map((id) => `${id}:${explicit[id]}`)
    .join('|');
}

/**
 * Build initial attendance from FHIR Appointment (Patient + RelatedPerson clients).
 */
export function getDefaultParticipantAttendanceFromAppointment(
  appointment: Appointment,
): Record<string, ParticipantAttendanceStatus> {
  const out: Record<string, ParticipantAttendanceStatus> = {};
  for (const p of appointment.participant ?? []) {
    const id = clientParticipantIdFromReference(p.actor?.reference);
    if (!id) continue;
    out[id] = resolveParticipantAttendanceFromAppointmentParticipant(p, appointment);
  }
  return out;
}

/**
 * Write note/QR attendance onto Appointment.participant extensions (by client id).
 * Required before shared-note fan-out: the bot reads Appointment, not the QR block.
 */
export function applyParticipantAttendanceByPatientIdToAppointment(
  appointment: Appointment,
  byPatientId: Record<string, string | undefined>,
): Appointment {
  let changed = false;
  const participants = (appointment.participant ?? []).map((p) => {
    const id = clientParticipantIdFromReference(p.actor?.reference);
    if (!id) return p;
    const raw = byPatientId[id];
    if (!raw) return p;
    const status = normalizeCalendarClientAttendanceStatus(raw) ?? raw;
    const next: AppointmentParticipant = { ...p };
    setParticipantAttendanceStatus(next, status);
    if (isCalendarClientAttendanceAbsent(status as CalendarClientAttendanceStatus)) {
      next.status = 'declined';
    } else if (isCalendarClientAttendancePresent(status as CalendarClientAttendanceStatus)) {
      next.status = 'accepted';
    }
    changed = true;
    return next;
  });
  return changed ? { ...appointment, participant: participants } : appointment;
}

/**
 * Remove the synthetic participant-attendance group from QR items (so Questionnaire form ignores it).
 */
export function stripParticipantAttendanceGroup(
  items: QuestionnaireResponseItem[] | undefined,
): QuestionnaireResponseItem[] {
  if (!items?.length) return [];
  return items.filter((i) => i.linkId !== PARTICIPANT_ATTENDANCE_GROUP_LINKID);
}

export type AttendanceRowMeta = { patientId: string; displayName: string };

function locationFromChildItem(child: QuestionnaireResponseItem): string | undefined {
  const fromSecondAnswer = child.answer?.[1]?.valueString?.trim();
  if (fromSecondAnswer) return fromSecondAnswer;
  return undefined;
}

/**
 * Read participant → location map from a saved QuestionnaireResponse.
 */
export function getParticipantLocationFromResponseItems(
  items: QuestionnaireResponseItem[] | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  const group = items?.find((i) => i.linkId === PARTICIPANT_ATTENDANCE_GROUP_LINKID);
  if (!group?.item) return out;
  for (const child of group.item) {
    const id = child.linkId ? parsePatientIdFromParticipantAttendanceLinkId(child.linkId) : undefined;
    if (!id) continue;
    const location = locationFromChildItem(child);
    if (location) out[id] = location;
  }
  return out;
}

/**
 * Build nested `item` block for participant attendance (sibling to template response items).
 */
export function buildParticipantAttendanceResponseItems(
  byPatientId: Record<string, ParticipantAttendanceStatus | undefined>,
  rowMeta: AttendanceRowMeta[],
  locationByPatientId: Record<string, string | undefined> = {},
): QuestionnaireResponseItem | null {
  const childItems: QuestionnaireResponseItem[] = [];
  for (const row of rowMeta) {
    const st = byPatientId[row.patientId] ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
    const location = locationByPatientId[row.patientId]?.trim();
    childItems.push({
      linkId: linkIdForPatient(row.patientId),
      text: row.displayName,
      answer: [
        { valueString: st },
        ...(location ? [{ valueString: location }] : []),
      ],
    });
  }
  if (childItems.length === 0) return null;
  return {
    linkId: PARTICIPANT_ATTENDANCE_GROUP_LINKID,
    text: 'Participant Attendance',
    item: childItems,
  };
}

/**
 * Merge or replace the participant group into the full response `item` array.
 */
export function mergeParticipantAttendanceIntoResponseItems(
  baseItems: QuestionnaireResponseItem[] | undefined,
  byPatientId: Record<string, ParticipantAttendanceStatus | undefined>,
  rowMeta: AttendanceRowMeta[],
  locationByPatientId: Record<string, string | undefined> = {},
): QuestionnaireResponseItem[] {
  const stripped = stripParticipantAttendanceGroup(baseItems);
  const block = buildParticipantAttendanceResponseItems(byPatientId, rowMeta, locationByPatientId);
  if (!block) return stripped;
  return [...stripped, block];
}

/**
 * Split QR items for read-only body: template content vs participant attendance.
 */
export function splitParticipantAttendanceFromResponseItems(
  items: QuestionnaireResponseItem[] | undefined,
): {
  templateItems: QuestionnaireResponseItem[];
  attendanceItem: QuestionnaireResponseItem | undefined;
} {
  if (!items?.length) {
    return { templateItems: [], attendanceItem: undefined };
  }
  const attendance = items.find((i) => i.linkId === PARTICIPANT_ATTENDANCE_GROUP_LINKID);
  if (!attendance) {
    return { templateItems: items, attendanceItem: undefined };
  }
  return {
    templateItems: items.filter((i) => i.linkId !== PARTICIPANT_ATTENDANCE_GROUP_LINKID),
    attendanceItem: attendance,
  };
}

/**
 * All patient participants for outcome checks: note attendance rows plus any other
 * patients on the Appointment not already listed.
 */
export function buildSessionParticipantAttendanceRows(
  attendanceRows: ReadonlyArray<{ patientId: string }>,
  appointment: Appointment | null | undefined,
): Array<{ patientId: string }> {
  const byId = new Map<string, { patientId: string }>();
  for (const row of attendanceRows) {
    byId.set(row.patientId, { patientId: row.patientId });
  }
  if (appointment) {
    for (const p of getAllClientParticipantsFromAppointment(appointment)) {
      const id = p.actorReference?.includes('/')
        ? p.actorReference.slice(p.actorReference.indexOf('/') + 1).trim()
        : undefined;
      if (id && !byId.has(id)) {
        byId.set(id, { patientId: id });
      }
    }
  }
  return [...byId.values()];
}

type AbsenceOutcomeKind = 'late-cancel' | 'no-show' | 'cancelled';

const ABSENCE_OUTCOME_BY_STATUS: Partial<Record<ParticipantAttendanceStatus, AbsenceOutcomeKind>> = {
  'client-late-cancelled': 'late-cancel',
  'client-no-show': 'no-show',
  'client-cancelled': 'cancelled',
  'clinician-cancelled': 'cancelled',
};

/**
 * When every session participant is absent (late cancelled, no show, or cancelled by the client
 * or clinician), return that outcome for the absence note template; differing reasons return
 * `mixed`. Any attended or unrecorded participant returns undefined so the session-type default is used.
 */
export function getUnanimousParticipantAttendanceOutcomeKind(
  rows: ReadonlyArray<{ patientId: string }>,
  attendanceByPatientId: Record<string, ParticipantAttendanceStatus | undefined>,
): AbsenceOutcomeKind | 'mixed' | undefined {
  if (rows.length === 0) return undefined;

  let kind: AbsenceOutcomeKind | 'mixed' | undefined;
  for (const row of rows) {
    const status =
      normalizeCalendarClientAttendanceStatus(attendanceByPatientId[row.patientId])
      ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
    const rowKind = ABSENCE_OUTCOME_BY_STATUS[status];
    if (!rowKind) return undefined;
    kind = kind === undefined || kind === rowKind ? rowKind : 'mixed';
  }
  return kind;
}
